import { useEffect, useMemo, useRef, useState } from 'react'
import { loadIntegrationConnections } from '../../../../services/linkHubConnectionService'
import type { StorageConnectionMetadata } from '../../../../types/schema'
import type { AnalysisTimeRangeKey } from '../../../../types/analysis'
import type { LiveModuleHeadline, LiveModuleRecord, LiveModuleSparkline } from '../LiveModuleCard'

export type UseIntegrationHealthModuleParams = {
  workspaceId: string
  timeRange?: AnalysisTimeRangeKey
  refreshKey?: number
  now?: Date
}

export type UseIntegrationHealthModuleResult = {
  headline: LiveModuleHeadline
  sparkline: LiveModuleSparkline
  records: LiveModuleRecord[]
  loading: boolean
  error: Error | null
}

const isConnected = (status: string) => status === 'ACTIVE' || status === 'CONNECTED'

export const useIntegrationHealthModule = ({
  workspaceId,
  timeRange = '7D',
  refreshKey,
  now = new Date(),
}: UseIntegrationHealthModuleParams): UseIntegrationHealthModuleResult => {
  const [connections, setConnections] = useState<StorageConnectionMetadata[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<Error | null>(null)
  const activeRequest = useRef(0)

  useEffect(() => {
    if (!workspaceId) {
      setConnections([])
      setLoading(false)
      setError(null)
      return undefined
    }
    const requestId = ++activeRequest.current
    setLoading(true)
    setError(null)
    void loadIntegrationConnections({ workspaceId })
      .then((result) => {
        if (requestId !== activeRequest.current) return
        setConnections(result.items)
        setLoading(false)
      })
      .catch((reason) => {
        if (requestId !== activeRequest.current) return
        setConnections([])
        setError(reason instanceof Error ? reason : new Error('Failed to load integration connections'))
        setLoading(false)
      })
    return () => { activeRequest.current += 1 }
  }, [workspaceId, refreshKey])

  return useMemo(() => {
    void timeRange
    void now
    const connected = connections.filter((connection) => isConnected(connection.status))
    const records: LiveModuleRecord[] = connected.slice(0, 5).map((connection) => ({
      id: connection.id,
      primary: connection.displayName || String(connection.provider),
      secondary: String(connection.status).toLowerCase(),
      numeric: 0,
      drillTarget: 'LINK',
    }))

    // Per Analysis Hub §2.2's Phase 3b decision, provider activity has no time-series yet; connected-provider growth is good.
    return {
      headline: { value: connected.length, label: 'providers' },
      sparkline: { points: [], direction: 'up-is-good' },
      records,
      loading,
      error,
    }
  }, [connections, loading, error, timeRange, now])
}
